import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Calendar as CalendarIcon } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { nb } from 'date-fns/locale';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Calendar Import Section
 * Lets Skyteleder/Admin/Super Admin import calendar events from .ics file or webcal:// URL.
 * Shows preview before confirming import. All imported events default to type "training".
 */
const CalendarImportSection = ({ user, onImported }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('file');
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('input'); // 'input' | 'preview'
  const [previewEvents, setPreviewEvents] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [skipDuplicates, setSkipDuplicates] = useState(true);

  const reset = () => {
    setStep('input');
    setFile(null);
    setUrl('');
    setPreviewEvents([]);
    setSelectedIds(new Set());
  };

  const handlePreview = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      if (mode === 'file') {
        if (!file) { toast.error('Velg en .ics-fil'); setLoading(false); return; }
        formData.append('file', file);
      } else {
        if (!url.trim()) { toast.error('Skriv inn en URL'); setLoading(false); return; }
        formData.append('url', url.trim());
      }
      const res = await axios.post(`${API_BASE}/api/calendar/import/preview`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const items = res.data.events || [];
      setPreviewEvents(items);
      const ids = new Set(items.map((e, i) => (e.is_duplicate ? null : i)).filter(i => i !== null));
      setSelectedIds(ids);
      setStep('preview');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Kunne ikke lese kalender');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (idx) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === previewEvents.length) {
      setSelectedIds(new Set());
    } else {
      const indices = previewEvents.map((e, i) => (skipDuplicates && e.is_duplicate) ? null : i).filter(i => i !== null);
      setSelectedIds(new Set(indices));
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const eventsToImport = previewEvents.filter((_, idx) => selectedIds.has(idx));
      const res = await axios.post(`${API_BASE}/api/calendar/import/confirm`,
        { events: eventsToImport, event_type: 'training' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Importerte ${res.data.inserted} aktiviteter`);
      reset();
      setOpen(false);
      if (onImported) onImported();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Import feilet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-4 p-4 bg-gray-800/50 rounded-lg border border-gray-600">
      <h5 className="font-medium text-white mb-2 flex items-center gap-2">
        <Upload className="w-4 h-4" />
        Importér kalender (.ics / webcal://)
      </h5>
      <p className="text-sm text-gray-400 mb-3">
        Migrér enkelt fra en eksisterende kalender. Last opp en .ics-fil eller hent fra en webcal://-URL.
        Alle importerte hendelser blir satt til typen <strong>Trening</strong>, og du kan endre type på de få som ikke er trening etterpå.
      </p>

      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="px-4 py-2 bg-yellow-500 text-gray-900 font-medium rounded-lg hover:bg-yellow-400 transition-colors flex items-center gap-2 text-sm"
          data-testid="open-calendar-import-btn"
        >
          <Upload className="w-4 h-4" />
          Start import
        </button>
      ) : (
        <div className="space-y-3">
          {step === 'input' && (
            <>
              <div className="flex gap-2 border-b border-gray-700">
                <button
                  onClick={() => setMode('file')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${mode === 'file' ? 'border-yellow-500 text-yellow-400' : 'border-transparent text-gray-400 hover:text-gray-200'}`}
                  data-testid="settings-import-mode-file"
                >
                  Last opp .ics-fil
                </button>
                <button
                  onClick={() => setMode('url')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${mode === 'url' ? 'border-yellow-500 text-yellow-400' : 'border-transparent text-gray-400 hover:text-gray-200'}`}
                  data-testid="settings-import-mode-url"
                >
                  Fra URL (webcal://)
                </button>
              </div>

              {mode === 'file' ? (
                <div>
                  <input
                    type="file"
                    accept=".ics,text/calendar"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="w-full text-sm text-gray-300 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-yellow-500 file:text-gray-900 file:font-medium hover:file:bg-yellow-400"
                    data-testid="settings-import-file-input"
                  />
                </div>
              ) : (
                <div>
                  <input
                    type="text"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="webcal://example.com/calendar.ics eller https://..."
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    data-testid="settings-import-url-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Webcal-URL hentes én gang (snapshot). Synkroniseres ikke automatisk.
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => { reset(); setOpen(false); }}
                  className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded-lg"
                  data-testid="settings-import-cancel-btn"
                >
                  Avbryt
                </button>
                <button
                  onClick={handlePreview}
                  disabled={loading}
                  className="px-4 py-2 text-sm bg-yellow-500 text-gray-900 font-medium rounded-lg hover:bg-yellow-400 disabled:opacity-50"
                  data-testid="settings-import-preview-btn"
                >
                  {loading ? 'Leser...' : 'Vis forhåndsvisning'}
                </button>
              </div>
            </>
          )}

          {step === 'preview' && (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm text-gray-300">
                  Fant <strong>{previewEvents.length}</strong> aktiviteter. Velg hvilke du vil importere:
                </p>
                <label className="flex items-center gap-2 text-xs text-gray-400">
                  <input
                    type="checkbox"
                    checked={skipDuplicates}
                    onChange={(e) => setSkipDuplicates(e.target.checked)}
                    data-testid="settings-import-skip-duplicates"
                  />
                  Hopp over duplikater
                </label>
              </div>

              <div className="flex justify-between items-center">
                <button onClick={toggleAll} className="text-xs text-yellow-400 hover:text-yellow-300">
                  {selectedIds.size === previewEvents.length ? 'Fjern alle' : 'Velg alle'}
                </button>
                <span className="text-xs text-gray-400">{selectedIds.size} valgt</span>
              </div>

              <div className="max-h-80 overflow-y-auto border border-gray-700 rounded-lg divide-y divide-gray-700">
                {previewEvents.length === 0 && (
                  <div className="p-6 text-center text-sm text-gray-400">
                    <CalendarIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    Ingen aktiviteter funnet i kilden.
                  </div>
                )}
                {previewEvents.map((ev, idx) => (
                  <label
                    key={idx}
                    className={`flex items-start gap-3 p-3 hover:bg-gray-700/40 cursor-pointer ${ev.is_duplicate ? 'opacity-60' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(idx)}
                      onChange={() => toggleSelect(idx)}
                      disabled={skipDuplicates && ev.is_duplicate}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-white truncate">{ev.title}</span>
                        {ev.is_duplicate && (
                          <span className="text-xs px-2 py-0.5 bg-orange-500/20 text-orange-300 rounded-full">Duplikat</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {format(parseISO(ev.start_datetime), 'EEEE d. MMM yyyy HH:mm', { locale: nb })}
                        {ev.location ? ` · ${ev.location}` : ''}
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              <div className="flex justify-between gap-2">
                <button
                  onClick={() => setStep('input')}
                  className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded-lg"
                  data-testid="settings-import-back-btn"
                >
                  ← Tilbake
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={loading || selectedIds.size === 0}
                  className="px-4 py-2 text-sm bg-yellow-500 text-gray-900 font-medium rounded-lg hover:bg-yellow-400 disabled:opacity-50"
                  data-testid="settings-import-confirm-btn"
                >
                  {loading ? 'Importerer...' : `Importér ${selectedIds.size} hendelser`}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CalendarImportSection;
